import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { useMemo } from 'react';
import { debounce } from 'lodash-es';
import { useStore, useStoreActions } from '~/contexts';
import { useQueryState } from '~/hooks/reactQuery/useQueryState';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { DEFAULT_SALES_TAX_CONFIG } from '~/types/salesTax';
import { QueryKeys } from '../../queryKeys';
import { useUpdateSalesTaxConfig } from '../useUpdateSalesTaxConfig/useUpdateSalesTaxConfig';
export const useSalesTaxConfig = function useSalesTaxConfig() {
    let { enabled = true, refreshOnHookMount = false } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const isVendor = useStore(selectors.user.isVendor);
    const { SALES_TAX } = useFeatureFlags();
    const salesTaxFlagEnabled = SALES_TAX === true;
    const { inputDebounceTime } = useAppConfig();
    const { data, isLoading, getQueryData, optimisticUpdate } = useQueryState({
        queryKey: [
            QueryKeys.SalesTaxConfig
        ],
        queryFn: async ()=>businessId == null ? DEFAULT_SALES_TAX_CONFIG : api.vendor.getSalesTax(businessId),
        enabled: enabled && isVendor && businessId != null && salesTaxFlagEnabled,
        refreshOnHookMount
    });
    const { updateSalesTaxConfig } = useUpdateSalesTaxConfig();
    const debouncedSaveSettings = useMemo(()=>debounce((next)=>{
            void updateSalesTaxConfig({
                kind: 'settings',
                settings: {
                    enabled: next.enabled,
                    displayMode: next.displayMode,
                    label: next.label
                }
            });
        }, inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500), [
        updateSalesTaxConfig
    ]);
    const config = data !== null && data !== void 0 ? data : DEFAULT_SALES_TAX_CONFIG;
    const salesTaxSupported = salesTaxFlagEnabled && config.enabled;
    const updateConfig = (patch)=>{
        var _getQueryData;
        const next = _object_spread({}, (_getQueryData = getQueryData()) !== null && _getQueryData !== void 0 ? _getQueryData : config, patch);
        optimisticUpdate(next);
        debouncedSaveSettings(next);
    };
    const createRate = async (preset)=>{
        const persisted = await updateSalesTaxConfig({
            kind: 'createRate',
            preset
        });
        if (persisted == null) {
            throw new Error('Sales tax rate was not persisted');
        }
        return persisted;
    };
    const editRate = async (next)=>{
        await updateSalesTaxConfig({
            kind: 'editRate',
            next
        });
    };
    return {
        config,
        salesTaxSupported,
        isLoading,
        updateConfig,
        createRate,
        editRate
    };
};
